import Loading from "@/components/breadcrumbs/Loading";
import {useState} from "react";
import DomainAllocation from "./shared/DomainAllocation";
import {DynamicDataSource, useDynamicDataSource} from "./dynamicDataSource";
import {defineMessage, MessageDescriptor, useIntl} from "react-intl";
import {FormattedPageTitle} from "@/components/pages";
import {useAuth} from "@/context/AuthProvider";
import {
    useCapabilitySetDomainAllocationQuery,
    useCapabilitySetDomainAllocationSuperuserQuery,
    useDeviceTypeSetDomainAllocationQuery,
    useDeviceTypeSetDomainAllocationSuperuserQuery,
    useUserPackageDomainAllocationQuery,
    useUserPackageDomainAllocationSuperuserQuery,
} from "../queries.generated";
import {AllocationType} from "./types";
import {DynamicDomainAllocationForm} from "./form";

const allocationTypeQueryMapping = {
    userPackages: {
        query: useUserPackageDomainAllocationQuery,
        superuserQuery: useUserPackageDomainAllocationSuperuserQuery,
    },
    capabilitySets: {
        query: useCapabilitySetDomainAllocationQuery,
        superuserQuery: useCapabilitySetDomainAllocationSuperuserQuery,
    },
    deviceTypeSets: {
        query: useDeviceTypeSetDomainAllocationQuery,
        superuserQuery: useDeviceTypeSetDomainAllocationSuperuserQuery,
    },
};

export type AllocationPageType = Extract<AllocationType, keyof typeof allocationTypeQueryMapping>;

type Props = {
    domainId: number;

    // Type of the allocation
    allocationType: AllocationPageType;

    label: MessageDescriptor;
    labelForAllocation: (allocationName: string) => MessageDescriptor;
};

type ContentProps = {
    dataSource: DynamicDataSource;
} & Props;

const Content = ({domainId, allocationType, label, dataSource, labelForAllocation}: ContentProps) => {
    const form = new DynamicDomainAllocationForm(domainId, allocationType, dataSource.data);
    const [enabled, setEnabled] = useState(true);
    const intl = useIntl();
    const auth = useAuth();
    const canSeeSystemAllocation = auth.isSuperuser;

    async function submit() {
        setEnabled(false);
        if (await form.submit()) {
            form.newInitialData(await dataSource.refetch());
        }
        setEnabled(true);
    }

    return (
        <div>
            <div style={{marginBottom: "20px"}}>
                <FormattedPageTitle
                    key={"title-" + label.id}
                    desc={defineMessage({
                        id: "domain_settings.allocations.title",
                        defaultMessage: "{type} allocations for domain {domain}",
                    })}
                    values={{type: intl.formatMessage(label), domain: dataSource.data.domainName}}
                />
                <DomainAllocation
                    key={"domain-allocation-" + label.id}
                    submit={submit}
                    reset={() => form.reset()}
                    hasChanged={form.hasChanged}
                    enabled={enabled}
                    update={(name, value) => form.updateAllocation(name, value)}
                    label={label}
                    labelForAllocation={labelForAllocation}
                    canSeeSystemAllocation={canSeeSystemAllocation}
                    allocations={form.values.allocations}
                    errors={form.errorsWithContext}
                />
            </div>
        </div>
    );
};

export const DomainAllocationSettings = ({domainId, allocationType, label, labelForAllocation}: Props) => {
    const dataSource = useDynamicDataSource(
        domainId,
        allocationTypeQueryMapping[allocationType].query,
        allocationTypeQueryMapping[allocationType].superuserQuery,
        allocationType,
    );

    if (dataSource.loading) return <Loading />;

    return (
        <Content
            domainId={domainId}
            allocationType={allocationType}
            label={label}
            dataSource={dataSource}
            labelForAllocation={labelForAllocation}
        />
    );
};
